import authService from './authService';
import { readData, readList, toQuery } from './apiResponse';
import type { AnnualReport, CashflowReport, ExpenseReport, ReportBasis, ReportEntry } from '../types/finance';

export interface ReportEntriesFilter {
  year: number;
  month: number;
  /** null/undefined = sem filtro de categoria; 0 = "Sem categoria". */
  category_id?: number | null;
  basis?: ReportBasis;
  /** Soma as filhas da categoria. */
  include_children?: boolean;
}

/**
 * Finanças → Relatórios (Fase 5): despesas do mês por categoria (com drill-down), anual e fluxo
 * de caixa. Valores de despesa vêm POSITIVOS; `basis=projected` inclui os previstos.
 */
class ReportService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /reports/expenses?year=&month=&basis= */
  async getExpenses(year: number, month: number, basis: ReportBasis = 'realized'): Promise<ExpenseReport> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/expenses${toQuery({ year, month, basis })}`);
    const r = await readData<ExpenseReport>(response, 'Falha ao carregar o relatório de despesas');
    return { ...r, categories: (r?.categories ?? []).map((c) => ({ ...c, children: c.children ?? [] })) };
  }

  /** GET /reports/expenses/transactions?year=&month=&category_id=&basis=&include_children=1 — drill-down (data desc). */
  async getExpenseEntries(filter: ReportEntriesFilter): Promise<ReportEntry[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/reports/expenses/transactions${toQuery({
        year: filter.year,
        month: filter.month,
        category_id: filter.category_id === null || filter.category_id === undefined ? undefined : String(filter.category_id),
        basis: filter.basis,
        include_children: filter.include_children ? 1 : undefined,
      })}`,
    );
    return readList<ReportEntry>(response, 'Falha ao carregar os lançamentos do relatório');
  }

  /** GET /reports/expenses/annual?year=&basis= */
  async getAnnual(year: number, basis: ReportBasis = 'realized'): Promise<AnnualReport> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/expenses/annual${toQuery({ year, basis })}`);
    const r = await readData<AnnualReport>(response, 'Falha ao carregar o relatório anual');
    return {
      ...r,
      months: r?.months ?? [],
      categories: r?.categories ?? [],
      expense_months: r?.expense_months ?? [],
      income_months: r?.income_months ?? [],
      net_months: r?.net_months ?? [],
    };
  }

  /** GET /reports/cashflow?start=AAAA-MM&end=AAAA-MM&account_id= (máx. 24 meses). */
  async getCashflow(start: string, end: string, accountId?: number | null): Promise<CashflowReport> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/cashflow${toQuery({ start, end, account_id: accountId || undefined })}`);
    const r = await readData<CashflowReport>(response, 'Falha ao carregar o fluxo de caixa');
    return { ...r, months: r?.months ?? [] };
  }
}

export const reportService = new ReportService();
export default reportService;
